import jmLogo from "../assets/img/jm-interim-transparent.png";
import { useEffect, useRef, useState, useCallback } from "react";
import { Modal, message } from "antd";
import { jsPDF } from "jspdf";
import * as pdfjsLib from "pdfjs-dist";
import {
  Loader2,
  Upload as UploadIcon,
  Eraser,
  Undo2,
  Trash2,
  Wand2,
  Check,
  Square,
  MousePointer2,
} from "lucide-react";
import axios from "../axios/AxiosInstance";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

const RENDER_WIDTH = 760;
const renderedDocuments = new Map();
let renderedSession = "";

const base64ToUint8Array = (base64) => {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const normalize = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const onlyDigits = (s) => String(s || "").replace(/\D/g, "");

const CvAnonymizerModal = ({
  open,
  candidate,
  initialFile,
  onConfirm,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [includeLogo, setIncludeLogo] = useState(false);
  const [pages, setPages] = useState([]); // [{ url, w, h, auto: [] }]
  const [boxes, setBoxes] = useState([]); // [{ page, x, y, w, h }] (ordonné)
  const [drawing, setDrawing] = useState(null); // { page, x, y, w, h }
  const [progress, setProgress] = useState("");
  const [sourceName, setSourceName] = useState("");
  const fileInputRef = useRef(null);
  const drawStart = useRef(null);

  const targets = useCallback(() => {
    const t = [];
    if (candidate?.firstname)
      t.push({ type: "text", v: normalize(candidate.firstname) });
    if (candidate?.lastname)
      t.push({ type: "text", v: normalize(candidate.lastname) });
    if (candidate?.email)
      t.push({ type: "text", v: normalize(candidate.email) });
    // Adresse : rue, ville et code postal (selon ce qui est disponible).
    if (candidate?.address && normalize(candidate.address).length >= 3)
      t.push({ type: "text", v: normalize(candidate.address) });
    if (candidate?.town && normalize(candidate.town).length >= 3)
      t.push({ type: "text", v: normalize(candidate.town) });
    const postCode = onlyDigits(candidate?.postCode);
    if (postCode && postCode.length >= 4)
      t.push({ type: "digits", v: postCode });
    if (candidate?.mobile)
      t.push({ type: "digits", v: onlyDigits(candidate.mobile) });
    const phone = onlyDigits(candidate?.phone);
    if (phone && phone.length >= 6) t.push({ type: "digits", v: phone });
    return t;
  }, [candidate]);

  const matchesTarget = useCallback(
    (str) => {
      const list = targets();
      if (!list.length) return false;
      const n = normalize(str);
      const d = onlyDigits(str);
      return list.some((tg) => {
        if (tg.type === "digits")
          return d.length >= 4 && (tg.v.includes(d) || d.includes(tg.v));
        return tg.v.length >= 2 && n.includes(tg.v);
      });
    },
    [targets],
  );

  const renderPdf = useCallback(
    async (data) => {
      const session = localStorage.getItem("accessToken") || "";
      if (renderedSession !== session) {
        renderedDocuments.clear();
        renderedSession = session;
      }
      const hash = crypto.subtle
        ? Array.from(
            new Uint8Array(await crypto.subtle.digest("SHA-256", data)),
          )
            .map((value) => value.toString(16).padStart(2, "0"))
            .join("")
        : null;
      const cacheKey = hash ? `${hash}:${JSON.stringify(targets())}` : null;
      if (cacheKey && renderedDocuments.has(cacheKey))
        return renderedDocuments.get(cacheKey);
      const loadingTask = pdfjsLib.getDocument({
        data,
        isEvalSupported: false,
      });
      const pdf = await loadingTask.promise;
      if (pdf.numPages > 30) {
        await loadingTask.destroy();
        throw new Error("CV limité à 30 pages");
      }
      const result = new Array(pdf.numPages);
      let completed = 0;
      const renderPage = async (p) => {
        const page = await pdf.getPage(p);
        const base = page.getViewport({ scale: 1 });
        const scale = RENDER_WIDTH / base.width;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport }).promise;

        // Détection auto (nom / tél / email)
        const auto = [];
        try {
          const tc = await page.getTextContent();
          for (const item of tc.items) {
            if (!item.str || !item.str.trim()) continue;
            if (
              !matchesTarget(item.str) &&
              !/@|https?:|www\.|linkedin|(?:\+?\d[\s.()-]*){9,}/i.test(item.str)
            )
              continue;
            const m = pdfjsLib.Util.transform(
              viewport.transform,
              item.transform,
            );
            const fontH = Math.hypot(m[2], m[3]) || 12;
            const x = m[4];
            const y = m[5] - fontH;
            const w = (item.width || 0) * scale;
            auto.push({
              x: Math.max(0, x - 3),
              y: Math.max(0, y - 3),
              w: w + 6,
              h: fontH + 6,
            });
          }
        } catch (e) {
          // pas de couche texte (CV scanné) → masquage auto indisponible
        }

        result[p - 1] = {
          url: canvas.toDataURL("image/jpeg", 0.92),
          w: canvas.width,
          h: canvas.height,
          auto,
        };
        page.cleanup();
        canvas.width = 0;
        canvas.height = 0;
        setProgress(
          `Prévisualisation : ${++completed}/${pdf.numPages} page(s)`,
        );
      };
      try {
        for (let start = 1; start <= pdf.numPages; start += 2)
          await Promise.all([
            renderPage(start),
            ...(start < pdf.numPages ? [renderPage(start + 1)] : []),
          ]);
        if (cacheKey) {
          renderedDocuments.set(cacheKey, result);
          while (renderedDocuments.size > 2)
            renderedDocuments.delete(renderedDocuments.keys().next().value);
        }
        return result;
      } finally {
        await loadingTask.destroy();
        setProgress("");
      }
    },
    [matchesTarget, targets],
  );

  const renderImage = useCallback(async (dataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, RENDER_WIDTH / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(img.width * scale);
        canvas.height = Math.floor(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve([
          {
            url: canvas.toDataURL("image/jpeg", 0.92),
            w: canvas.width,
            h: canvas.height,
            auto: [],
          },
        ]);
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  }, []);

  const loadFromBase64 = useCallback(
    async (base64) => {
      const header = base64.substring(0, 5);
      if (header === "JVBER") {
        return renderPdf(base64ToUint8Array(base64));
      }
      if (header.startsWith("/9j/")) {
        return renderImage(`data:image/jpeg;base64,${base64}`);
      }
      if (header.startsWith("iVBOR")) {
        return renderImage(`data:image/png;base64,${base64}`);
      }
      throw new Error("Format de fichier non supporté");
    },
    [renderPdf, renderImage],
  );

  const loadCandidateCv = useCallback(() => {
    // Les pressentis sont des "available-candidat" : leur _id n'est pas celui
    // du candidat. Le CV est stocké sous l'id du candidat d'origine
    // (sourceCandidate). On le résout, avec repli sur _id.
    const candidateId =
      (candidate?.sourceCandidate &&
      typeof candidate.sourceCandidate === "object"
        ? candidate.sourceCandidate._id
        : candidate?.sourceCandidate) || candidate?._id;
    if (!candidateId) return;
    setLoading(true);
    setBoxes([]);
    axios
      .get(`cv/${candidateId}`)
      .then(async (res) => {
        const base64 = res.data?.data?.replace(/^data:[^,]+,/, "");
        if (!base64) {
          message.info(
            "Ce candidat n'a pas de CV enregistré. Importez un fichier.",
          );
          setPages([]);
          return;
        }
        const rendered = await loadFromBase64(base64);
        setPages(rendered);
        setBoxes(
          rendered.flatMap((pg, page) =>
            pg.auto.map((box) => ({ ...box, page })),
          ),
        );
        setSourceName("CV du candidat");
      })
      .catch((e) => {
        console.log(e);
        message.error("Impossible de charger le CV du candidat");
      })
      .finally(() => setLoading(false));
  }, [candidate, loadFromBase64]);

  const loadFile = useCallback(
    (file) => {
      if (!file) return;
      setLoading(true);
      setBoxes([]);
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          if (file.type === "application/pdf") {
            const arr = new Uint8Array(reader.result);
            const rendered = await renderPdf(arr);
            setPages(rendered);
            setBoxes(
              rendered.flatMap((pg, page) =>
                pg.auto.map((box) => ({ ...box, page })),
              ),
            );
          } else if (file.type && file.type.startsWith("image/")) {
            const dataUrl = `data:${file.type};base64,${btoa(
              new Uint8Array(reader.result).reduce(
                (acc, b) => acc + String.fromCharCode(b),
                "",
              ),
            )}`;
            setPages(await renderImage(dataUrl));
          } else {
            message.error("Format non supporté (PDF, JPG ou PNG)");
            return;
          }
          setSourceName(file.name);
        } catch (err) {
          console.log(err);
          message.error("Impossible de lire le fichier");
        } finally {
          setLoading(false);
        }
      };
      reader.onerror = () => {
        setLoading(false);
        message.error("Impossible de lire le fichier");
      };
      reader.readAsArrayBuffer(file);
    },
    [renderPdf, renderImage],
  );

  useEffect(() => {
    if (open) {
      setPages([]);
      setBoxes([]);
      setDrawing(null);
      if (initialFile) {
        loadFile(initialFile);
      } else {
        loadCandidateCv();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    loadFile(file);
    e.target.value = "";
  };

  // Raccourci clavier : Ctrl/Cmd + Z pour annuler le dernier cache.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        setBoxes((prev) => (prev.length ? prev.slice(0, -1) : prev));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // --- Dessin des rectangles de caviardage ---
  const getRelativePos = (evt, el) => {
    const rect = el.getBoundingClientRect();
    return {
      x: evt.clientX - rect.left,
      y: evt.clientY - rect.top,
    };
  };

  const onPointerDown = (pageIdx, evt) => {
    evt.preventDefault();
    const pos = getRelativePos(evt, evt.currentTarget);
    drawStart.current = { page: pageIdx, x: pos.x, y: pos.y };
    setDrawing({ page: pageIdx, x: pos.x, y: pos.y, w: 0, h: 0 });
  };

  const onPointerMove = (pageIdx, evt) => {
    if (!drawStart.current || drawStart.current.page !== pageIdx) return;
    const pos = getRelativePos(evt, evt.currentTarget);
    const s = drawStart.current;
    setDrawing({
      page: pageIdx,
      x: Math.min(s.x, pos.x),
      y: Math.min(s.y, pos.y),
      w: Math.abs(pos.x - s.x),
      h: Math.abs(pos.y - s.y),
    });
  };

  const onPointerUp = () => {
    if (drawing && drawing.w > 4 && drawing.h > 4) {
      setBoxes((prev) => [
        ...prev,
        {
          page: drawing.page,
          x: drawing.x,
          y: drawing.y,
          w: drawing.w,
          h: drawing.h,
        },
      ]);
    }
    drawStart.current = null;
    setDrawing(null);
  };

  // Annule le tout dernier cache placé (toutes pages confondues).
  const undoLastGlobal = useCallback(() => {
    setBoxes((prev) => (prev.length ? prev.slice(0, -1) : prev));
  }, []);

  const clearPage = (pageIdx) => {
    setBoxes((prev) => prev.filter((b) => b.page !== pageIdx));
  };

  const clearAll = () => setBoxes([]);

  const applyAuto = () => {
    const additions = [];
    pages.forEach((pg, idx) => {
      (pg.auto || []).forEach((b) => additions.push({ ...b, page: idx }));
    });
    if (additions.length === 0) {
      message.info(
        "Aucune donnée détectée automatiquement (CV scanné ou sans texte). Caviardez manuellement.",
      );
      return;
    }
    setBoxes((prev) => [...prev, ...additions]);
    message.success(`${additions.length} zone(s) masquée(s) automatiquement`);
  };

  const totalBoxes = boxes.length;

  const handleExport = async () => {
    if (!pages.length) return;
    setExporting(true);
    try {
      const logo = includeLogo ? await new Promise((resolve, reject) => {
        const image = new Image(); image.onload = () => resolve(image); image.onerror = reject; image.src = jmLogo;
      }) : null;
      const headerHeight = includeLogo ? 70 : 0;
      let pdf;
      for (let i = 0; i < pages.length; i++) {
        const pg = pages[i];
        const canvas = document.createElement("canvas");
        canvas.width = pg.w;
        canvas.height = pg.h;
        const ctx = canvas.getContext("2d");
        // image de fond
        const img = await new Promise((resolve, reject) => {
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = pg.url;
        });
        ctx.drawImage(img, 0, 0, pg.w, pg.h);
        // caviardage permanent (aplati dans l'image)
        ctx.fillStyle = "#ffffff";
        boxes
          .filter((b) => b.page === i)
          .forEach((b) => {
            ctx.fillRect(b.x, b.y, b.w, b.h);
          });
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        const orientation = pg.w > pg.h ? "l" : "p";
        if (i === 0) {
          pdf = new jsPDF({
            unit: "px",
            format: [pg.w, pg.h + 64 + headerHeight],
            orientation,
          });
        } else {
          pdf.addPage([pg.w, pg.h + 64 + headerHeight], orientation);
        }
        pdf.addImage(dataUrl, "JPEG", 0, headerHeight, pg.w, pg.h);
        if (logo) {
          const scale = Math.min(130 / logo.naturalWidth, 50 / logo.naturalHeight);
          pdf.addImage(logo, "PNG", 16, 10, logo.naturalWidth * scale, logo.naturalHeight * scale);
        }
        pdf.setFontSize(10);
        pdf.text(
          `JM Interim | Candidat JM-${String(candidate?._id || "").toUpperCase()}`,
          16,
          pg.h + headerHeight + 20,
        );
        pdf.text(
          [
            localStorage.getItem("firstname"),
            localStorage.getItem("lastname"),
            localStorage.getItem("userEmail"),
            localStorage.getItem("phone"),
          ]
            .filter((v) => v && v !== "undefined")
            .join(" | "),
          16,
          pg.h + headerHeight + 40,
        );
      }
      const blob = pdf.output("blob");
      const fileName = `CV-JM-${String(candidate?._id || "").toUpperCase()}.pdf`;
      const file = new File([blob], fileName, { type: "application/pdf" });
      onConfirm(file);
      message.success("CV anonymisé prêt à être envoyé");
      onClose();
    } catch (e) {
      console.log(e);
      message.error("Échec de la génération du CV anonymisé");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      open={open}
      footer={null}
      width={920}
      zIndex={10001}
      centered
      onCancel={onClose}
      styles={{
        body: { padding: 0 },
        content: { padding: 0, borderRadius: 16, overflow: "hidden" },
      }}
    >
      <div className="flex flex-col max-h-[90vh]">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4">
          <h3 className="text-white font-bold text-lg flex items-center gap-2">
            <Eraser className="w-5 h-5" />
            Anonymiser le CV
          </h3>
          <p className="text-gray-300 text-sm mt-0.5">
            Caviardez les informations personnelles avant d'envoyer le CV au
            client. Masquez aussi la photo et les liens personnels en
            cliquant-glissant sur les zones concernées.
          </p>
        </div>

        {/* Barre d'outils */}
        <div className="flex flex-wrap items-center gap-2 px-6 py-3 border-b border-gray-100 bg-white">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            <UploadIcon className="w-4 h-4" />
            Importer un fichier
          </button>
          <button
            onClick={loadCandidateCv}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            Recharger le CV du candidat
          </button>
          <button
            onClick={applyAuto}
            disabled={!pages.length}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 text-sm font-medium hover:bg-indigo-100 transition-colors disabled:opacity-50"
          >
            <Wand2 className="w-4 h-4" />
            Masquer auto (nom, tél, email)
          </button>
          <button
            onClick={undoLastGlobal}
            disabled={totalBoxes === 0}
            title="Annuler le dernier cache (Ctrl/Cmd+Z)"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors disabled:opacity-50"
          >
            <Undo2 className="w-4 h-4" />
            Annuler
          </button>
          <button
            onClick={clearAll}
            disabled={totalBoxes === 0}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Tout effacer
          </button>

          <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            <MousePointer2 className="w-3.5 h-3.5" />
            Cliquez-glissez pour masquer · Ctrl/Cmd+Z pour annuler
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 text-gray-500">
              <Loader2 className="w-10 h-10 animate-spin mb-3" />
              <p className="font-medium" role="status">
                {progress || "Chargement du document…"}
              </p>
            </div>
          ) : !pages.length ? (
            <div className="flex flex-col items-center justify-center py-24 text-gray-400">
              <Square className="w-12 h-12 mb-3" />
              <p className="font-medium">Aucun document chargé</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="mt-3 inline-flex items-center gap-2 text-indigo-600 font-medium hover:underline"
              >
                <UploadIcon className="w-4 h-4" /> Importer un fichier
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-6">
              {pages.map((pg, idx) => (
                <div key={idx} className="relative">
                  <div className="absolute -top-3 left-0 z-10 flex gap-2">
                    {boxes.some((b) => b.page === idx) && (
                      <button
                        onClick={() => clearPage(idx)}
                        className="inline-flex items-center gap-1 bg-white shadow border border-gray-200 text-red-600 text-xs px-2 py-1 rounded-md hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" /> Effacer la page
                      </button>
                    )}
                  </div>
                  <div
                    className="relative bg-white rounded-lg shadow-md ring-1 ring-gray-200 overflow-hidden select-none touch-none cursor-crosshair"
                    style={{ width: pg.w, height: pg.h }}
                    onPointerDown={(e) => onPointerDown(idx, e)}
                    onPointerMove={(e) => onPointerMove(idx, e)}
                    onPointerUp={onPointerUp}
                    onPointerLeave={onPointerUp}
                  >
                    <img
                      src={pg.url}
                      alt={`Page ${idx + 1}`}
                      width={pg.w}
                      height={pg.h}
                      draggable={false}
                      className="block pointer-events-none"
                    />
                    {boxes
                      .map((b, bi) => ({ ...b, bi }))
                      .filter((b) => b.page === idx)
                      .map((b) => (
                        <div
                          key={b.bi}
                          className="absolute bg-white"
                          style={{
                            left: b.x,
                            top: b.y,
                            width: b.w,
                            height: b.h,
                          }}
                        />
                      ))}
                    {drawing && drawing.page === idx && (
                      <div
                        className="absolute bg-white/80 border border-dashed border-blue-400"
                        style={{
                          left: drawing.x,
                          top: drawing.y,
                          width: drawing.w,
                          height: drawing.h,
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 bg-white px-6 py-3">
          <label className="flex items-center gap-3 text-sm font-medium text-slate-700"><input type="checkbox" checked={includeLogo} onChange={event => setIncludeLogo(event.target.checked)} disabled={exporting} />Ajouter le logo JM Intérim sur chaque page</label>
          {includeLogo && <div className="mt-3 rounded-lg border border-slate-200 p-3"><img src={jmLogo} alt="Logo JM Intérim ajouté en en-tête" className="h-12 w-auto object-contain" /><p className="mt-2 text-xs text-slate-500">Le logo sera placé au-dessus du CV, sans recouvrir son contenu.</p></div>}
        </div>
        {/* Pied */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-white">
          <span className="text-sm text-gray-500">
            {sourceName && `${sourceName} · `}
            {totalBoxes} zone(s) masquée(s)
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={exporting}
              className="px-5 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold"
            >
              Annuler
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || !pages.length}
              className="px-5 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold flex items-center gap-2 disabled:opacity-60"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Valider & joindre
            </button>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        onChange={handleUpload}
        className="hidden"
      />
    </Modal>
  );
};

export default CvAnonymizerModal;
